<template>
  <div class="tz-page min-h-screen bg-#F5F7FA">
    <!-- 用户信息 -->
    <div class="px-12px pt-24px">
      <UserItem :data="userData" :padding="false" />
    </div>

    <!-- 转赠金额卡片 -->
    <div class="mx-12px mt-24px bg-white rounded-8px p-16px">
      <div class="text-#333 text-14px">转赠金额</div>
      <van-field
        v-model="amount"
        type="number"
        placeholder="请输入金额"
        class="mt-32px !p-0 amount-field"
        :border="false"
        :min="MIN_AMOUNT"
      >
        <template #left-icon>
          <span class="text-32px text-222 font-500">¥&nbsp;</span>
        </template>
      </van-field>
    </div>

    <!-- 错误提示 -->
    <div v-if="showMinError" class="mx-12px mt-12px text-14px text-[#FF4D4F]">转赠金额不能小于 0.01 元</div>

    <!-- 转赠账户卡片 -->
    <div class="mx-12px mt-12px bg-white rounded-8px px-16px py-12px">
      <div class="flex items-center justify-between">
        <span class="text-#333 text-14px">转赠账户</span>
        <span class="text-666 text-14px">{{ depositName }}</span>
      </div>
      <div class="text-14px text-right mt-10px">可用余额 ¥{{ formatAmount(balance) }}</div>
    </div>
    <div v-if="showError" class="mx-12px mt-12px text-14px text-[#FF4D4F]">可用余额不足，请修改转赠金额</div>

    <!-- 底部按钮 -->
    <div class="fixed bottom-0 left-0 right-0 px-12px pb-24px pt-12px bg-#F5F7FA">
      <van-button round block class="h-48px" :disabled="isDisabled" color="#FD7F5C" @click="handleConfirm">
        确认转赠
      </van-button>
    </div>

    <!-- 确认转赠弹窗 -->
    <TransferConfirmDialog
      v-model:show="showConfirmDialog"
      :nickname="userData.nickname"
      :amount="parseFloat(amount) || 0"
      :loading="confirmLoading"
      @confirm="handleTransferConfirm"
    />

    <!-- 转赠成功 -->
    <TransferSuccess v-model:show="showSuccess" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { Field as vanField } from 'vant'
import { useRoute } from 'vue-router'
import UserItem from '@/components/UserItem.vue'
import TransferConfirmDialog from './components/TransferConfirmDialog.vue'
import TransferSuccess from './components/TransferSuccess.vue'
import usePage from '@/hooks/usePage'
import { useUserStore } from '@/store/user'
import { transferDeposit, getPreFundInvestBalance } from '@/api/yck'
const route = useRoute()
const { navigateTo } = usePage()
const userStore = useUserStore()
const depositName = computed(() => userStore.getDepositName)

// 用户数据 - 从 URL 参数获取
const userData = ref({
  avatarUrl: '',
  nickname: '',
  level: 0,
  levelName: '',
  mobile: '',
  inviterLevelName: '',
  inviterMobile: '',
  puid: ''
})

// 解析 URL 中的用户数据
if (route.query.userData) {
  try {
    const data = JSON.parse(decodeURIComponent(route.query.userData))
    userData.value = { ...userData.value, ...data }
  } catch (e) {
    console.error('解析用户数据失败', e)
  }
}

// 转赠金额
const amount = ref('')

// 最小转赠金额
const MIN_AMOUNT = 0.01

// 可用余额
const balance = ref(1.0)

// 确认弹窗显示状态
const showConfirmDialog = ref(false)

// 转赠成功显示状态
const showSuccess = ref(false)

// 是否显示错误提示（余额不足）
const showError = computed(() => {
  const amountNum = parseFloat(amount.value) || 0
  return amountNum > balance.value
})

// 是否显示最小金额错误提示
const showMinError = computed(() => {
  const amountNum = parseFloat(amount.value) || 0
  return amountNum > 0 && amountNum < MIN_AMOUNT
})

// 按钮是否禁用
const isDisabled = computed(() => {
  return showError.value || showMinError.value || !amount.value
})

// 格式化金额
const formatAmount = value => {
  return value.toFixed(2)
}

// 点击确认转赠按钮，打开确认弹窗
const handleConfirm = () => {
  if (isDisabled.value) return
  showConfirmDialog.value = true
}

// 确认弹窗点击确认后的回调
const confirmLoading = ref(false)
const handleTransferConfirm = async () => {
  try {
    confirmLoading.value = true
    const resData = await transferDeposit({
      puid: userData.value.puid,
      amount: parseFloat(amount.value) || 0
    })
    confirmLoading.value = false
    if (resData) {
      // 转赠成功，关闭弹窗并刷新数据
      showConfirmDialog.value = false
      showSuccess.value = true
    }
  } catch (error) {
    showSuccess.value = false
    confirmLoading.value = false
  }
}
onMounted(async () => {
  // 初始化时获取用户预存款余额
  const resData = await getPreFundInvestBalance()
  if (resData) {
    // 更新可用余额
    balance.value = resData.balance || 0
  }
})
</script>

<style lang="less" scoped>
.amount-field {
  :deep(.van-field__control) {
    font-size: 28px;
    font-weight: 500;
    color: #222;
  }
  :deep(.van-field__control::placeholder) {
    font-size: 28px;
    font-weight: 500;
    color: #d8d8d8;
  }
}
</style>
